import { Component, HostListener, OnInit } from '@angular/core';
import { AuthService } from './Services/auth.service';
import { NgIf } from '@angular/common';
import { environment } from '../environments/environment';
import { UnauthenticatedLayoutComponent } from './Component/Unauth/unauthenticated-layout/unauthenticated-layout.component';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { SignupComponent } from './Component/Unauth/signup/signup.component';
import { HomeComponent } from './Component/home/home.component';
import { AuthenticatedLayoutComponent } from './Component/Auth/authenticated-layout/authenticated-layout.component';
import { NavbarService } from './Services/navbar.service';
import { CustomizeService } from './Services/Customize_settings/customize.service';
import { FirmSelectionService } from './Services/firm-selection.service';
import { SimpleUnauthComponent } from './Component/Unauth/simple-unauth/simple-unauth.component';
import { AdminSettingsService } from './Services/Admin_Settings/admin-settings.service';
import { GstLayoutComponent } from './Component/Auth/gst-layout/gst-layout.component';

@Component({
  selector: 'app-root',
  imports: [
    NgIf,
    UnauthenticatedLayoutComponent,
    SignupComponent,
    HomeComponent,
    AuthenticatedLayoutComponent,
    SimpleUnauthComponent,
    GstLayoutComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {

  title: string = environment.appName;

  loginState!: string;

  signupState!: string;

  homeState!: string;

  mainlogin: boolean = false;
  simplelogin: boolean = false;
  loginTypeLoaded: boolean = false;

  isLoading = false;

  constructor(
    private authService: AuthService,
    public router: Router,
    private navbarService: NavbarService,
    private customizeService: CustomizeService,
    private firmSelectionService: FirmSelectionService,
    private adminSettingsService: AdminSettingsService,
  ) { }

  ngOnInit(): void {
    this.getLoginType();

    // ✅ GLOBAL LOADER
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.isLoading = true;
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.isLoading = false;
      }
    });

    // ✅ HANDLE FIRM SELECTION
    this.firmSelectionService.selectedFirmName$.subscribe((firm: any) => {

      if (!firm) return;

      // ✅ Skip ALL firm-related logic for Karigar / CA but for the CA also show the firm name code edit by pratik
      if (this.authService.isKalakaar()) {
        return;
      }

      // Do not call protected firm/settings APIs on login (or without a token)
      if (!this.authService.isAuthenticated || this.isUnauthPage) {
        return;
      }

      // 🔥 Load favicon when firm changes
      if (firm?.id) {
        this.loadFaviconForFirm(firm.id);
      }

      // 🔹 Load navbar settings
      if (firm.id) {
        this.loadNavbarSettingsForFirm(firm.id);
      }

      const url = this.cleanUrl;
      const currentPath = window.location.pathname;
      const isGst = this.isGstFirm(firm.status, firm.name);

      // 🟢 REGISTERED FIRM (GST)
      if (isGst) {
        const isSharedPage = this.isAccountsPageCustom(url) || this.isAccountsPageCustom(currentPath) || url === '/ca-account' || currentPath === '/ca-account';

        // Redirect only if the current page (according to router) AND the browser path are both non-GST
        // AND it's not a shared page (like accounts, daybook, etc.)
        if (this.isNonGstPage(url) && this.isNonGstPage(currentPath) && !isSharedPage && !this.isUnauthPage) {
          this.router.navigate(['/invoice-gst-panel']);
        }
        return;
      }

      // 🔵 NON-REGISTERED FIRM (NON-GST)
      // Redirect only if the current page (according to router) AND the browser path are both GST-only
      if (this.isGstOnlyPage(url) && this.isGstOnlyPage(currentPath)) {
        this.router.navigate(['/home']);
      }
    });

    // 🔥 LISTEN FOR LIVE LOGO UPDATES
    this.customizeService.logo$.subscribe((logo) => {
      if (logo) {
        this.updateFavicon(logo);
      }
    });

    // 🔥 LISTEN FOR LOGIN TYPE UPDATES
    this.adminSettingsService.loginType$.subscribe((type) => {
      this.mainlogin = type !== 'simple';
      this.simplelogin = type === 'simple';
    });
  }

  /**
   * If the browser restores a cached page after logout, send the user to login.
   * While still logged in, Back between panels is unchanged.
   */
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent): void {
    if (!event.persisted) {
      return;
    }
    this.redirectIfLoggedOut();
  }

  @HostListener('window:popstate')
  onPopState(): void {
    this.redirectIfLoggedOut();
  }

  private redirectIfLoggedOut(): void {
    if (this.authService.isAuthenticated || this.isUnauthPage || this.isSignupPage) {
      return;
    }
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  loadFaviconForFirm(firmId: number) {
    this.customizeService.getUserSettings(firmId).subscribe({
      next: (res: any) => {
        const logoUrl = res.data?.logo_url || res.data?.logo;

        if (logoUrl) {
          const finalUrl = logoUrl + '?v=' + new Date().getTime();
          this.updateFavicon(finalUrl);
          this.customizeService.setLogo(finalUrl);
        }
      }
    });
  }

  updateFavicon(iconUrl: string) {
    let link: HTMLLinkElement | null =
      document.querySelector("link[rel*='icon']");

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    link.type = 'image/png';
    link.href = iconUrl;
  }

  isGstFirm(status: any, name: string): boolean {
    return this.firmSelectionService.isGstFirm(status, name);
  }

  /**
   * Derived from the currently selected firm — not a stale flag.
   * Login used to set the firm while still on /login, so a stored boolean
   * never flipped to GST and Accounts opened the non-GST layout.
   */
  get isRegistered(): boolean {
    const firm = this.firmSelectionService.getselectedFirmName();
    return !!firm && this.isGstFirm(firm.status, firm.name);
  }

  private get cleanUrl(): string {
    return this.router.url.split('?')[0];
  }

  private isGstOnlyPage(url: string): boolean {
    return (
      url.startsWith('/invoice-gst') ||
      [
        '/invoice-gst-panel',
        '/purchase-bill',
        '/purchase-report',
        '/gst-bill-report',
        '/gst-settings/gst-inv-layout-settings',
        '/gst-settings',
        '/gst-settings/whatsapp-instance',
        '/gst-customer-list',
        '/invoice-panel',
        '/sale-report'
      ].some(r => url.startsWith(r))
    );
  }

  private isNonGstPage(url: string): boolean {
    return !this.isGstOnlyPage(url);
  }

  getLoginType() {
    this.adminSettingsService.getAdminSettings().subscribe({
      next: (response) => {
        this.loginTypeLoaded = true;
      },
      error: (err) => {
        console.error('Failed to load admin settings on app init:', err);
        // Fallback is already handled by initial value of BehaviorSubject ('simple')
        this.loginTypeLoaded = true;
      }
    });
  }

  loadNavbarSettingsForFirm(firmId: number): void {
    this.customizeService.getUserSettings(firmId).subscribe({
      next: (response) => {
        const settings = response.data;
        if (settings) {
          const submenuVisibility = settings.submenu_visibility || {};
          const mainMenuVisibility = settings.mainmenu_visibility || {};
          this.navbarService.setAllVisibilities(submenuVisibility);
          this.navbarService.setMainMenuVisibilities?.(mainMenuVisibility);
        }
      },
      error: (err) => {
        console.error('Failed to load navbar settings on app init:', err);
      }
    });
  }

  get isUnauthPage(): boolean {
    const unauthRoutes = [
      '/login',
      '/forgot-password',
      '/verify-otp',
      '/update-password',
      '/validate-otp',
    ];
    return unauthRoutes.includes(this.router.url);
  }

  get isSignupPage(): boolean {
    return this.router.url === '/signup';
  }

  get isHomePage(): boolean {
    return this.router.url === '/home' || this.router.url === '/';
  }

  get isLoggedIn() {
    return this.authService.isAuthenticated;
  }

  get isAuthPage(): boolean {
    // If it's a GST page OR an Accounts page for a GST firm, it's NOT a standard auth page
    if (this.isGstInvoicePage) return false;
    if (this.isAccountsPage && this.isRegistered) return false;

    return !this.isUnauthPage && !this.isSignupPage && !this.isHomePage;
  }

  get isGstInvoicePage(): boolean {
    const url = this.router.url.split('?')[0];
    // Standard GST pages
    const isStandardGstPage = /^\/invoice-gst\/\d+$/.test(url) ||
      [
        '/invoice-gst-panel',
        '/purchase-bill',
        '/purchase-report',
        '/gst-bill-report',
        '/gst-settings/gst-inv-layout-settings',
        '/gst-settings',
        '/gst-settings/whatsapp-instance',
        '/gst-customer-list',
        '/invoice-panel',
        '/sale-report'
      ].includes(url);

    return isStandardGstPage;
  }

  get isAccountsPage(): boolean {
    return this.isAccountsPageCustom();
  }

  isAccountsPageCustom(path?: string): boolean {
    const url = (path || this.router.url).split('?')[0];
    return url.startsWith('/accounts') || url === '/account-ledger' || url === '/daybook' || url === '/trail-balance' || url === '/balance-sheet' || url === '/profit-loss' || url === '/logs';
  }
}



